import { createFileRoute, notFound, redirect } from "@tanstack/react-router";
import { ToolShell } from "@/components/tools/ToolShell";
import { getFrToolBySlug } from "@/lib/fr-tools-catalog";
import { getFrToolContent } from "@/lib/fr-tool-content";
import { ToolBody } from "../free-tools/$slug";
import { getEsSlugForEnSlug } from "@/lib/es-tools-catalog";
import { getDeSlugForEnSlug } from "@/lib/de-tools-catalog";
import { getPtBrSlugForEnSlug } from "@/lib/pt-br-tools-catalog";

export const Route = createFileRoute("/fr/$slug")({
  loader: ({ params }) => {
    if (params.slug === "pdf-tools") throw redirect({ to: "/fr/pdf-tools", statusCode: 301 });
    if (params.slug === "a-propos" || params.slug === "about") throw redirect({ to: "/fr/about", statusCode: 301 });
    if (params.slug === "contact") throw redirect({ to: "/fr/contact", statusCode: 301 });
    if (params.slug === "politique-de-confidentialite" || params.slug === "privacy-policy") throw redirect({ to: "/fr/privacy-policy", statusCode: 301 });
    if (params.slug === "conditions-d-utilisation" || params.slug === "terms-of-use") throw redirect({ to: "/fr/terms-of-use", statusCode: 301 });
    if (params.slug === "mentions-legales" || params.slug === "disclaimer") throw redirect({ to: "/fr/disclaimer", statusCode: 301 });

    const tool = getFrToolBySlug(params.slug);
    if (!tool) throw notFound();
    if (tool.slug !== params.slug) {
      throw redirect({ to: "/fr/$slug", params: { slug: tool.slug }, statusCode: 301 });
    }
    return { tool };
  },
  head: ({ params }) => {
    const tool = getFrToolBySlug(params.slug);
    if (!tool) return { meta: [{ title: "Outil — Picamini.online" }] };

    const content = getFrToolContent(tool.slug);
    const canonicalUrl = `https://picamini.online/fr/${tool.slug}`;
    const enUrl = `https://picamini.online/${tool.enSlug}`;
    const esSlug = getEsSlugForEnSlug(tool.enSlug);
    const esUrl = `https://picamini.online/es/${esSlug}`;
    const deSlug = getDeSlugForEnSlug(tool.enSlug);
    const deUrl = `https://picamini.online/de/${deSlug}`;
    const ptBrSlug = getPtBrSlugForEnSlug(tool.enSlug);
    const ptBrUrl = `https://picamini.online/pt-br/${ptBrSlug}`;

    const webAppSchema = {
      "@context": "https://schema.org",
      "@type": "WebApplication",
      "name": `${tool.name} - Picamini.online`,
      "url": canonicalUrl,
      "description": content.seoDescription,
      "applicationCategory": "EducationalApplication",
      "operatingSystem": "All",
      "browserRequirements": "Nécessite JavaScript. Nécessite HTML5.",
      "inLanguage": "fr",
      "offers": {
        "@type": "Offer",
        "price": "0",
        "priceCurrency": "USD",
      },
      "featureList": content.features || [],
    };

    const breadcrumbSchema = {
      "@context": "https://schema.org",
      "@type": "BreadcrumbList",
      "itemListElement": [
        {
          "@type": "ListItem",
          "position": 1,
          "name": "Accueil",
          "item": "https://picamini.online/fr/",
        },
        {
          "@type": "ListItem",
          "position": 2,
          "name": "Outils Gratuits",
          "item": "https://picamini.online/fr/",
        },
        {
          "@type": "ListItem",
          "position": 3,
          "name": tool.name,
          "item": canonicalUrl,
        },
      ],
    };

    const faqSchema = content.faq && content.faq.length > 0 ? {
      "@context": "https://schema.org",
      "@type": "FAQPage",
      "mainEntity": content.faq.map((item) => ({
        "@type": "Question",
        "name": item.q,
        "acceptedAnswer": {
          "@type": "Answer",
          "text": item.a,
        },
      })),
    } : null;

    const scripts: Array<{ type: string; children: string }> = [
      { type: "application/ld+json", children: JSON.stringify(webAppSchema) },
      { type: "application/ld+json", children: JSON.stringify(breadcrumbSchema) },
    ];
    if (faqSchema) {
      scripts.push({ type: "application/ld+json", children: JSON.stringify(faqSchema) });
    }

    return {
      meta: [
        { title: content.seoTitle },
        { name: "description", content: content.seoDescription },
        { name: "keywords", content: tool.keywords.join(", ") },
        { property: "og:site_name", content: "Picamini.online" },
        { property: "og:title", content: content.seoTitle },
        { property: "og:description", content: content.seoDescription },
        { property: "og:type", content: "website" },
        { property: "og:url", content: canonicalUrl },
        { property: "og:locale", content: "fr_FR" },
        { property: "og:image", content: "https://picamini.online/og-image.png" },
        { name: "twitter:card", content: "summary_large_image" },
        { name: "twitter:title", content: content.seoTitle },
        { name: "twitter:description", content: content.seoDescription },
        { name: "robots", content: "index, follow, max-image-preview:large, max-snippet:-1" },
      ],
      links: [
        { rel: "canonical", href: canonicalUrl },
        { rel: "alternate", hrefLang: "en", href: enUrl },
        { rel: "alternate", hrefLang: "es", href: esUrl },
        { rel: "alternate", hrefLang: "de", href: deUrl },
        { rel: "alternate", hrefLang: "pt-BR", href: ptBrUrl },
        { rel: "alternate", hrefLang: "fr", href: canonicalUrl },
        { rel: "alternate", hrefLang: "x-default", href: enUrl },
      ],
      scripts,
    };
  },
  component: FrToolPage,
});

function FrToolPage() {
  const { tool } = Route.useLoaderData();
  const content = getFrToolContent(tool.slug);

  return (
    <ToolShell tool={tool} content={content} lang="fr">
      <ToolBody key={tool.enSlug} slug={tool.enSlug} />
    </ToolShell>
  );
}
